import React, { useState, useEffect } from 'react';
import MeasurementForm, { MeasurementData } from '../shared/MeasurementForm';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { X, ArrowLeft, ArrowRight, Check, AlertCircle, User, Camera, Share2, Mail, Smartphone, Plus } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import AddCustomerModal from './AddCustomerModal';
import { generateQRCodeWithLogo } from '@/utils/qrCodeWithLogo';

interface BusinessOrderFormProps {
  onClose: () => void;
  onSubmit: (orderData: any) => void;
}

interface Customer {
  id: string; // Changed from number to string to match usage
  name: string;
  email?: string;
  phone?: string;
  measurements?: any; // Customer-level measurements JSON
  photo_front_url?: string;
  photo_back_url?: string;
  photo_side_url?: string;
  total_orders?: number;
  last_order_date?: string | null;
}

const BusinessOrderForm: React.FC<BusinessOrderFormProps> = ({ onClose, onSubmit }) => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [showQRCode, setShowQRCode] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [inventory, setInventory] = useState<any[]>([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [savingMeasurements, setSavingMeasurements] = useState(false);
  const [measurementsSaved, setMeasurementsSaved] = useState(false);
  const [measurementsSaveTimeout, setMeasurementsSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [appDownloadLink, setAppDownloadLink] = useState<string>('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [isCreatingInventory, setIsCreatingInventory] = useState(false);
  const [inventoryFormData, setInventoryFormData] = useState({
    name: '',
    category: 'fabric',
    current_stock: 0,
    unit: 'pieces', // Backend default is 'pieces', not 'meters'
    selling_price: 0,
    cost_per_unit: 0,
    colors: [] as string[] // Array of colors
  });
  
  const [orderData, setOrderData] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: {} as {
      address?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
      coordinates?: {
        lat: number;
        lng: number;
      };
    },
    order_type: 'new',
    priority: 'medium',
    due_date: '',
    special_instructions: '',
    garments: [
      {
        id: 1,
        type: '',
        material: '',
        material_name: '',
        color: '',
        quantity: 0.000,
        price_per_unit: 0,
        price_unit: 'meters',
        stitching_type: '',
        stitching_cost: 0,
        base_price: 0,
        material_cost: 0,
        labor_cost: 0,
        total_cost: 0,
        measurements: {
          neck: '',
          chest: '',
          waist: '',
          sleeve_length: '',
          inseam: '',
          shoulder_width: '',
          armhole: '',
          bicep: '',
          wrist: '',
          hip: '',
          thigh: '',
          knee: '',
          ankle: '',
          back_length: ''
        }
      }
    ],
    measurements: {
      neck: '',
      chest: '',
      waist: '',
      sleeve_length: '',
      inseam: '',
      shoulder_width: '',
      armhole: '',
      bicep: '',
      wrist: '',
      hip: '',
      thigh: '',
      knee: '',
      ankle: '',
      back_length: ''
    },
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0,
    include_gst: false,
    photos: {
      front: null as File | { url?: string; name?: string } | null,
      back: null as File | { url?: string; name?: string } | null,
      side: null as File | { url?: string; name?: string } | null,
      optional: [] as File[]
    }
  });

  const garmentTypes = [
    { id: 'suit', name: 'Suit', icon: '👔', basePrice: 15000 },
    { id: 'dress', name: 'Dress', icon: '👗', basePrice: 8000 },
    { id: 'shirt', name: 'Shirt', icon: '👕', basePrice: 3000 },
    { id: 'pants', name: 'Pants', icon: '👖', basePrice: 4000 },
    { id: 'blazer', name: 'Blazer', icon: '🧥', basePrice: 12000 },
    { id: 'kurta', name: 'Kurta', icon: '🥻', basePrice: 5000 },
    { id: 'sherwani', name: 'Sherwani', icon: '👘', basePrice: 25000 },
    { id: 'lehenga', name: 'Lehenga', icon: '👗', basePrice: 20000 },
    { id: 'saree_blouse', name: 'Saree Blouse', icon: '👚', basePrice: 2000 },
    { id: 'waistcoat', name: 'Waistcoat', icon: '🎽', basePrice: 6000 },
    { id: 'trouser', name: 'Trouser', icon: '👖', basePrice: 3500 },
    { id: 'jacket', name: 'Jacket', icon: '🧥', basePrice: 10000 }
  ];

  const priorities = [
    { id: 'low', name: 'Low', color: '#10B981', days: 21, multiplier: 1.0 },
    { id: 'medium', name: 'Medium', color: '#F59E0B', days: 14, multiplier: 1.2 },
    { id: 'high', name: 'High', color: '#EF4444', days: 7, multiplier: 1.5 },
    { id: 'urgent', name: 'Urgent', color: '#DC2626', days: 3, multiplier: 2.0 }
  ];

  // Helper function to get photo URL (handles both File objects and URL strings)
  const getPhotoUrl = (photo: File | { url?: string; name?: string } | null): string | null => {
    if (!photo) return null;
    if (photo instanceof File) {
      return URL.createObjectURL(photo);
    }
    if (typeof photo === 'object' && 'url' in photo && photo.url) {
      return photo.url;
    }
    return null;
  };

  // Photo upload handlers
  const handlePhotoUpload = (view: 'front' | 'back' | 'side', file: File | null) => {
    setOrderData(prev => ({
      ...prev,
      photos: {
        ...prev.photos,
        [view]: file
      }
    }));
  };

  const handleOptionalPhotoUpload = (files: FileList | null) => {
    if (files) {
      setOrderData(prev => ({
        ...prev,
        photos: {
          ...prev.photos,
          optional: Array.from(files)
        }
      }));
    }
  };

  // Helper functions for garment management
  const addGarment = () => {
    const newGarment = {
      id: Date.now(),
      type: '',
      material: '',
      material_name: '',
      color: '',
      quantity: 0.000,
      price_per_unit: 0,
      price_unit: 'meters',
      stitching_type: '',
      stitching_cost: 0,
      base_price: 0,
      material_cost: 0,
      labor_cost: 0,
      total_cost: 0,
      measurements: {
        neck: '',
        chest: '',
        waist: '',
        sleeve_length: '',
        inseam: '',
        shoulder_width: '',
        armhole: '',
        bicep: '',
        wrist: '',
        hip: '',
        thigh: '',
        knee: '',
        ankle: '',
        back_length: ''
      }
    };
    
    setOrderData(prev => ({
      ...prev,
      garments: [...prev.garments, newGarment]
    }));
  };

  const removeGarment = (garmentId: number) => {
    setOrderData(prev => ({
      ...prev,
      garments: prev.garments.filter(g => g.id !== garmentId)
    }));
  };

  const updateGarment = (garmentId: number, field: string, value: any) => {
    setOrderData(prev => ({
      ...prev,
      garments: prev.garments.map(garment => {
        if (garment.id === garmentId) {
          const updatedGarment = { ...garment };
          
          if (field.includes('.')) {
            const [parent, child] = field.split('.');
            updatedGarment[parent as keyof typeof garment] = {
              ...updatedGarment[parent as keyof typeof garment],
              [child]: value
            };
          } else {
            updatedGarment[field as keyof typeof garment] = value;
            
            // Recalculate costs when material, quantity, price_per_unit, or stitching cost changes
            if (field === 'material' || field === 'quantity' || field === 'price_per_unit' || field === 'stitching_cost') {
              // Use price_per_unit entered by user instead of inventory price
              updatedGarment.material_cost = (updatedGarment.price_per_unit || 0) * (updatedGarment.quantity || 0);
              
              // Round stitching cost to 2 decimals
              const roundedStitchingCost = Math.round((updatedGarment.stitching_cost || 0) * 100) / 100;
              updatedGarment.stitching_cost = roundedStitchingCost;
              
              // Only material cost + stitching cost (no base price or labor cost)
              updatedGarment.total_cost = updatedGarment.material_cost + updatedGarment.stitching_cost;
            }
            
            // When material_name changes, reset color and material
            if (field === 'material_name') {
              updatedGarment.color = '';
              updatedGarment.material = '';
            }
            
            // When color changes, find matching inventory item
            if (field === 'color' && updatedGarment.material_name) {
              const matchingItem = inventory.find(
                item => item.name === updatedGarment.material_name && 
                (item.color || '').toLowerCase() === (value || '').toLowerCase()
              );
              if (matchingItem) {
                updatedGarment.material = matchingItem.id.toString();
              }
            }
          }
          
          return updatedGarment;
        }
        return garment;
      })
    }));
  };

  const calculateOrderTotal = () => {
    const subtotal = orderData.garments.reduce((sum, garment) => sum + garment.total_cost, 0);
    
    let taxAmount = 0;
    let totalAmount = subtotal;
    
    if (orderData.include_gst) {
      // GST 5% added on top of subtotal
      taxAmount = subtotal * 0.05;
      totalAmount = subtotal + taxAmount;
    }
    
    setOrderData(prev => ({
      ...prev,
      subtotal,
      tax_amount: taxAmount,
      total_amount: totalAmount
    }));
  };

  // Load customers and inventory on component mount
  useEffect(() => {
    loadCustomers();
    loadInventory();
  }, []);

  // Pre-select customer from URL params when customers are loaded
  useEffect(() => {
    const customerIdFromUrl = searchParams.get('customerId');
    if (customerIdFromUrl && customers.length > 0 && !orderData.customer_id) {
      const preSelectedCustomer = customers.find(
        c => c.id === customerIdFromUrl
      );
      if (preSelectedCustomer) {
        handleCustomerSelect(preSelectedCustomer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, searchParams]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (measurementsSaveTimeout) {
        clearTimeout(measurementsSaveTimeout);
      }
    };
  }, [measurementsSaveTimeout]);

  // Generate app download link and QR code when Step 6 is reached
  useEffect(() => {
    if (currentStep === 6 && orderData.customer_id && !qrCodeDataUrl) {
      generateAppDownloadInfo();
    }
  }, [currentStep, orderData.customer_id, qrCodeDataUrl]);

  // Generate app download link and QR code
  const generateAppDownloadInfo = async () => {
    try {
      // Generate customer linking link if customer exists
      let downloadUrl = '';
      
      if (orderData.customer_id && token) {
        try {
          const response = await fetch(`${API_BASE}/api/tailors/customers/generate-link`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              tailor_id: user?.id,
              tailor_name: user?.name || user?.email,
              expires_in_hours: 168 // 7 days
            })
          });

          if (response.ok) {
            const data = await response.json();
            downloadUrl = data.link_url || '';
          }
        } catch (_error) {
          console.error('Error generating customer linking link:', error);
        }
      }

      // Fallback to generic app download page
      if (!downloadUrl) {
        const frontendUrl = typeof window !== 'undefined' ? window.location.origin : 'https://master-ji.com';
        downloadUrl = `${frontendUrl}/download-app?customer=${orderData.customer_email || orderData.customer_id}`;
      }

      setAppDownloadLink(downloadUrl);

      // Generate QR code with MasterJi logo
      const qrDataUrl = await generateQRCodeWithLogo(downloadUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        logoSizeRatio: 0.2
      });
      setQrCodeDataUrl(qrDataUrl);
    } catch (_error) {
      console.error('Error generating QR code:', error);
      // Fallback to a simple download link
      const frontendUrl = typeof window !== 'undefined' ? window.location.origin : 'https://master-ji.com';
      const fallbackUrl = `${frontendUrl}/download-app`;
      setAppDownloadLink(fallbackUrl);
    }
  };

  // Send email with download link
  const handleSendEmail = async () => {
    if (!orderData.customer_email) {
      setError('Customer email is required to send download link');
      return;
    }

    if (!token) {
      setError('Authentication required');
      return;
    }

    setSendingEmail(true);
    setEmailSent(false);
    setError('');

    try {
      // Generate email content
      const emailSubject = `Download MasterJi App - Track Your Order ${orderData.customer_name ? `for ${orderData.customer_name}` : ''}`;
      const emailBody = `
Hello${orderData.customer_name ? ` ${orderData.customer_name}` : ''},

Your order has been created successfully!

Download the MasterJi mobile app to:
• Track your order progress in real-time
• View order details and measurements
• Communicate with your tailor via chat
• Schedule appointments and fittings
• Receive notifications for updates

Download Link: ${appDownloadLink || 'Link will be available soon'}

${appDownloadLink ? `Or scan the QR code in your order confirmation to download the app.` : ''}

Best regards,
MasterJi Team
      `.trim();

      // Call backend to send email
      const response = await fetch(`${API_BASE}/api/send-app-download-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_email: orderData.customer_email,
          customer_name: orderData.customer_name,
          subject: emailSubject,
          body: emailBody,
          download_link: appDownloadLink,
          qr_code_data: qrCodeDataUrl
        })
      });

      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 5000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to send email' }));
        setError(errorData.detail || 'Failed to send email. Please try again.');
      }
    } catch (_error) {
      console.error('Error sending email:', error);
      setError('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    if (!appDownloadLink) {
      setError('Download link not available. Please try again.');
      return;
    }

    try {
      await navigator.clipboard.writeText(appDownloadLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (_error) {
      console.error('Error copying to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = appDownloadLink;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 3000);
      } catch (_err) {
        setError('Failed to copy link. Please copy manually: ' + appDownloadLink);
      }
      document.body.removeChild(textArea);
    }
  };

  // Recalculate totals when garments or GST inclusion changes
  useEffect(() => {
    calculateOrderTotal();
  }, [orderData.garments, orderData.include_gst]);

  const loadCustomers = async () => {
    if (!token) return;
    
    setLoadingCustomers(true);
    try {
      // Load customers from dedicated endpoint (includes customers without orders)
      const response = await fetch(`${API_BASE}/api/tailors/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const customersData = await response.json();
        let customersFromAPI: any = customersData?.customers ?? customersData ?? [];

        // If API returned an object keyed by id (not an array), convert it to an array
        if (!Array.isArray(customersFromAPI)) {
          if (customersFromAPI && typeof customersFromAPI === 'object') {
            customersFromAPI = Object.values(customersFromAPI);
          } else {
            customersFromAPI = [];
          }
        }

        // Convert API format to our Customer interface
        const convertedCustomers: Customer[] = customersFromAPI.map((customer: any) => ({
          id: String(customer.id ?? ''),
          name: customer.name || 'Unknown Customer',
          email: customer.email || '',
          phone: customer.phone || '',
          total_orders: customer.order_count || 0,
          last_order_date: customer.last_order_date || null,
          measurements: customer.measurements, // Customer-level measurements
          photo_front_url: customer.photo_front_url,
          photo_back_url: customer.photo_back_url,
          photo_side_url: customer.photo_side_url
        }));
        
        setCustomers(convertedCustomers);
      } else if (response.status === 404) {
        // Try compatibility route
        console.log('Primary customers endpoint not found, trying compatibility route...');
        const compatResponse = await fetch(`${API_BASE}/api/customers`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (compatResponse.ok) {
          const customersData = await compatResponse.json();
          let customersFromAPI: any = customersData?.customers ?? customersData ?? [];

          // If API returned an object keyed by id (not an array), convert it to an array
          if (!Array.isArray(customersFromAPI)) {
            if (customersFromAPI && typeof customersFromAPI === 'object') {
              customersFromAPI = Object.values(customersFromAPI);
            } else {
              customersFromAPI = [];
            }
          }

          // Convert API format to our Customer interface
          const convertedCustomers: Customer[] = customersFromAPI.map((customer: any) => ({
            id: String(customer.id ?? ''),
            name: customer.name || 'Unknown Customer',
            email: customer.email || '',
            phone: customer.phone || '',
            total_orders: customer.order_count || 0,
            last_order_date: customer.last_order_date || null
          }));
          
          setCustomers(convertedCustomers);
        } else {
          console.error('Failed to load customers from compatibility route:', compatResponse.status);
        }
      } else {
        console.error('Failed to load customers:', response.status);
      }
    } catch (_error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadInventory = async () => {
    if (!token) return;
    
    setLoadingInventory(true);
    try {
      const response = await fetch(`${API_BASE}/api/tailors/inventory`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let inventoryItems: any = data?.items ?? data ?? [];

        // If API returned an object keyed by id (not an array), convert it to an array
        if (!Array.isArray(inventoryItems)) {
          if (inventoryItems && typeof inventoryItems === 'object') {
            inventoryItems = Object.values(inventoryItems);
          } else {
            inventoryItems = [];
          }
        }

        // Filter for materials/fabrics only, but also include items without category if they might be materials
        // Show all active inventory items - let user decide what to use
        const materials = inventoryItems.filter((item: any) => {
          // Include all active items, or filter by category/name if we want to be more specific
          const isActive = item?.is_active !== false;
          const category = String(item?.category ?? '').toLowerCase();
          const name = String(item?.name ?? '').toLowerCase();
          const hasMaterialCategory =
            category.includes('fabric') ||
            category.includes('material') ||
            category.includes('cloth') ||
            category.includes('textile');
          const hasMaterialName =
            name.includes('fabric') ||
            name.includes('material') ||
            name.includes('cloth') ||
            name.includes('textile');

          // Include if active and (has material category/name OR category is empty/null - assume it might be material)
          return isActive && (hasMaterialCategory || hasMaterialName || !item?.category || item?.category === '');
        });

        setInventory(materials);
      } else {
        console.error('Failed to load inventory:', response.status);
      }
    } catch (_error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleCreateInventory = async () => {
    if (!token || !inventoryFormData.name.trim()) {
      setError('Please enter a material name');
      return;
    }

    setIsCreatingInventory(true);
    setError('');

    try {
      // If colors are provided, create separate inventory items for each color
      const colorsToCreate = inventoryFormData.colors.length > 0 
        ? inventoryFormData.colors.filter(c => c.trim() !== '')
        : [null]; // If no colors, create one item without color

      const createdItems = [];
      const failedItems: Array<{ color: string | null; error: string }> = [];
      
      for (const color of colorsToCreate) {
        try {
          // Build payload matching backend schema (InventoryItemCreateRequest)
          const payload: any = {
            name: inventoryFormData.name.trim(),
            category: inventoryFormData.category,
            current_stock: inventoryFormData.current_stock || 0,
            minimum_stock: 5.0, // Backend default is 5.0
            unit: inventoryFormData.unit
          };
          
          // Add color if provided
          if (color && color.trim()) {
            payload.color = color.trim();
          }
          
          // Only add optional fields if they have meaningful values
          if (inventoryFormData.selling_price && inventoryFormData.selling_price > 0) {
            payload.selling_price = inventoryFormData.selling_price;
          }
          if (inventoryFormData.cost_per_unit && inventoryFormData.cost_per_unit > 0) {
            payload.cost_per_unit = inventoryFormData.cost_per_unit;
          }

          const response = await fetch(`${API_BASE}/api/tailors/inventory`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          if (response.ok) {
            const data = await response.json();
            createdItems.push({ color: color || null, item: data });
          } else {
            let errorMessage = `Failed to create inventory item${color ? ` for color ${color}` : ''}`;
            try {
              const errorData = await response.json();
              errorMessage = errorData.detail || errorData.message || errorMessage;
            } catch (parseError) {
              // If JSON parsing fails, use status text
              errorMessage = `Error ${response.status}: ${response.statusText}`;
            }
            failedItems.push({ color: color || null, error: errorMessage });
          }
        } catch (error: any) {
          // Catch individual item errors and continue with others
          const errorMessage = error.message || `Failed to create inventory item${color ? ` for color ${color}` : ''}`;
          failedItems.push({ color: color || null, error: errorMessage });
        }
      }

      // Refresh inventory list if any items were created
      if (createdItems.length > 0) {
        await loadInventory();
      }

      // Show success/error messages
      if (failedItems.length > 0) {
        const errorMessages = failedItems.map(f => 
          f.color ? `Failed to create inventory item for color ${f.color}: ${f.error}` : `Failed to create inventory item: ${f.error}`
        ).join('\n\n');
        
        if (createdItems.length > 0) {
          setError(`Successfully created ${createdItems.length} item(s).\n\n${errorMessages}`);
        } else {
          setError(errorMessages);
        }
      } else {
        // All items created successfully
        // Reset form and close modal
        setInventoryFormData({
          name: '',
          category: 'fabric',
          current_stock: 0,
          unit: 'pieces',
          selling_price: 0,
          cost_per_unit: 0,
          colors: []
        });
        setIsInventoryModalOpen(false);
      }
    } catch (error: any) {
      console.error('Error creating inventory:', error);
      setError(error.message || 'Failed to create inventory item. Please try again.');
    } finally {
      setIsCreatingInventory(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setOrderData(prev => {
        const updated = {
          ...prev,
          [parent]: {
            ...prev[parent as keyof typeof prev],
            [child]: value
          }
        };
        
        // Auto-save measurements if they're being changed
        if (parent === 'measurements' && updated.customer_id) {
          saveMeasurements(updated.customer_id, updated.measurements);
        }
        
        return updated;
      });
    } else {
      setOrderData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Fetch measurements for a customer from the API
  const fetchCustomerMeasurements = async (customerId: string, customer?: Customer) => {
    if (!token) return null;
    
    setLoadingMeasurements(true);
    try {
      // First, try to find legacy customer ID by email if customer object is available
      let legacyCustomerId: string | null = null;
      
      if (customer?.email) {
        try {
          // Try to get legacy customer by email from /api/tailors/customers response
          // The customer list might have a legacy_customer_id field, or we need to search
          const customersResponse = await fetch(`${API_BASE}/api/tailors/customers`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (customersResponse.ok) {
            const customersData = await customersResponse.json();
            const customers = customersData.customers || [];
            const matchingCustomer = customers.find((c: any) => 
              c.email === customer.email || String(c.id) === customerId
            );
            
            // Check if there's a legacy_customer_id or customer_id field
            if (matchingCustomer?.customer_id) {
              legacyCustomerId = String(matchingCustomer.customer_id);
            } else if (matchingCustomer?.legacy_customer_id) {
              legacyCustomerId = String(matchingCustomer.legacy_customer_id);
            } else if (matchingCustomer?.id && matchingCustomer.source === 'legacy_only') {
              // If it's a legacy-only customer, the id IS the legacy customer ID
              legacyCustomerId = String(matchingCustomer.id);
            }
          }
        } catch (_err) {
          console.warn('Could not fetch customer list for legacy ID lookup:', err);
        }
      }
      
      // Try the measurements-photos endpoint with legacy customer ID if found
      // Otherwise try with the user ID (might work if the endpoint supports both)
      const measurementIdsToTry = legacyCustomerId ? [legacyCustomerId, customerId] : [customerId];
      
      for (const idToTry of measurementIdsToTry) {
        try {
          const response = await fetch(`${API_BASE}/api/measurements-photos/customers/${idToTry}/measurements`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            // The API returns measurements in an array, get the latest one
            if (data.measurements && data.measurements.length > 0) {
              const latestMeasurement = data.measurements[0];
              // Handle both flat format (direct fields) and nested format
              const measurementData = latestMeasurement.measurements || latestMeasurement;
              
              // Convert API format to our form format
              return {
                neck: measurementData.neck?.toString() || '',
                chest: measurementData.chest?.toString() || '',
                waist: measurementData.waist?.toString() || '',
                sleeve_length: measurementData.sleeve_length?.toString() || '',
                inseam: measurementData.inseam?.toString() || '',
                shoulder_width: measurementData.shoulder?.toString() || measurementData.shoulder_width?.toString() || '',
                armhole: measurementData.armhole?.toString() || '',
                bicep: measurementData.bicep?.toString() || '',
                wrist: measurementData.wrist?.toString() || '',
                hip: measurementData.hips?.toString() || measurementData.hip?.toString() || '',
                thigh: measurementData.thigh?.toString() || '',
                knee: measurementData.knee?.toString() || '',
                ankle: measurementData.ankle?.toString() || '',
                back_length: measurementData.back_length?.toString() || ''
              };
            }
          } else if (response.status === 404) {
            // If 404, customer has no measurements yet - this is normal for new customers
            // Silently continue without logging to console
            // The browser network log will still show it, but that's expected behavior
            continue;
          } else {
            // Log other errors but continue
            console.warn(`Measurements endpoint returned ${response.status} for customer ID ${idToTry}`);
          }
        } catch (fetchError) {
          // Network or other errors - continue silently
          // Don't log as this is expected for customers without measurements
          continue;
        }
      }
      
      // Fallback: check if customer object has measurements
      if (customer?.measurements) {
        // Handle both object format and flat format
        if (typeof customer.measurements === 'object') {
          return {
            neck: customer.measurements.neck?.toString() || '',
            chest: customer.measurements.chest?.toString() || '',
            waist: customer.measurements.waist?.toString() || '',
            sleeve_length: customer.measurements.sleeve_length?.toString() || '',
            inseam: customer.measurements.inseam?.toString() || '',
            shoulder_width: customer.measurements.shoulder_width?.toString() || customer.measurements.shoulder?.toString() || '',
            armhole: customer.measurements.armhole?.toString() || '',
            bicep: customer.measurements.bicep?.toString() || '',
            wrist: customer.measurements.wrist?.toString() || '',
            hip: customer.measurements.hip?.toString() || customer.measurements.hips?.toString() || '',
            thigh: customer.measurements.thigh?.toString() || '',
            knee: customer.measurements.knee?.toString() || '',
            ankle: customer.measurements.ankle?.toString() || '',
            back_length: customer.measurements.back_length?.toString() || ''
          };
        }
      }
      
      return null;
    } catch (_error) {
      console.error('Error fetching customer measurements:', error);
      return null;
    } finally {
      setLoadingMeasurements(false);
    }
  };

  // Auto-save measurements to backend (debounced)
  const saveMeasurements = async (customerId: string, measurements: any) => {
    if (!token || !customerId) return;
    
    // Clear existing timeout
    if (measurementsSaveTimeout) {
      clearTimeout(measurementsSaveTimeout);
    }
    
    // Set new timeout for debounced save
    const timeout = setTimeout(async () => {
      setSavingMeasurements(true);
      setMeasurementsSaved(false);
      
      try {
        // Convert measurements to the format expected by the API
        const measurementData: any = {};
        Object.entries(measurements).forEach(([key, value]) => {
          if (value && value.toString().trim() !== '') {
            const numValue = parseFloat(value.toString());
            if (!isNaN(numValue)) {
              // Map form field names to API field names
              const apiKey = key === 'shoulder_width' ? 'shoulder' : 
                           key === 'hip' ? 'hips' : 
                           key;
              measurementData[apiKey] = numValue;
            }
          }
        });
        
        // Try updating via PUT /api/customers/{customer_id}
        const response = await fetch(`${API_BASE}/api/customers/${customerId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            measurements: measurementData
          })
        });
        
        if (response.ok) {
          setMeasurementsSaved(true);
          // Clear success message after 3 seconds
          setTimeout(() => setMeasurementsSaved(false), 3000);
        } else {
          console.error('Failed to save measurements:', response.status);
        }
      } catch (_error) {
        console.error('Error saving measurements:', error);
      } finally {
        setSavingMeasurements(false);
      }
    }, 1000); // Wait 1 second after user stops typing
    
    setMeasurementsSaveTimeout(timeout);
  };

  const handleCustomerSelect = async (customer: Customer) => {
    // Load customer measurements from API
    const fetchedMeasurements = await fetchCustomerMeasurements(customer.id, customer);
    
    setOrderData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_phone: customer.phone || '',
      customer_email: customer.email || '',
      // Load customer-level measurements (use fetched measurements or fallback to customer.measurements)
      measurements: fetchedMeasurements || {
        neck: customer.measurements?.neck?.toString() || prev.measurements.neck,
        chest: customer.measurements?.chest?.toString() || prev.measurements.chest,
        waist: customer.measurements?.waist?.toString() || prev.measurements.waist,
        sleeve_length: customer.measurements?.sleeve_length?.toString() || prev.measurements.sleeve_length,
        inseam: customer.measurements?.inseam?.toString() || prev.measurements.inseam,
        shoulder_width: customer.measurements?.shoulder_width?.toString() || customer.measurements?.shoulder?.toString() || prev.measurements.shoulder_width,
        armhole: customer.measurements?.armhole?.toString() || prev.measurements.armhole,
        bicep: customer.measurements?.bicep?.toString() || prev.measurements.bicep,
        wrist: customer.measurements?.wrist?.toString() || prev.measurements.wrist,
        hip: customer.measurements?.hip?.toString() || customer.measurements?.hips?.toString() || prev.measurements.hip,
        thigh: customer.measurements?.thigh?.toString() || prev.measurements.thigh,
        knee: customer.measurements?.knee?.toString() || prev.measurements.knee,
        ankle: customer.measurements?.ankle?.toString() || prev.measurements.ankle,
        back_length: customer.measurements?.back_length?.toString() || prev.measurements.back_length
      },
      // Load customer photos if they exist
      photos: {
        ...prev.photos,
        front: customer.photo_front_url ? { url: customer.photo_front_url, name: 'front.jpg' } : prev.photos.front,
        back: customer.photo_back_url ? { url: customer.photo_back_url, name: 'back.jpg' } : prev.photos.back,
        side: customer.photo_side_url ? { url: customer.photo_side_url, name: 'side.jpg' } : prev.photos.side
      }
    }));
  };

  const handleCustomerCreated = (customer: Customer) => {
    // Add the newly created customer to the customers list
    setCustomers(prev => {
      // Check if customer already exists
      const exists = prev.some(c => c.id === customer.id);
      if (exists) {
        return prev.map(c => c.id === customer.id ? customer : c);
      }
      return [customer, ...prev];
    });
    
    // Automatically select the newly created customer
    handleCustomerSelect(customer);
  };

  const calculateDueDate = (priority: string) => {
    const priorityObj = priorities.find(p => p.id === priority);
    if (!priorityObj) return '';
    
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + priorityObj.days);
    return dueDate.toISOString().split('T')[0];
  };

  const calculateAmount = () => {
    const garmentType = garmentTypes.find(g => g.id === orderData.garment_type);
    const priority = priorities.find(p => p.id === orderData.priority);
    
    if (!garmentType || !priority) return 0;
    
    return Math.round(garmentType.basePrice * priority.multiplier);
  };

  const handleNext = () => {
    // Validate required photos before proceeding from step 3 (photo upload)
    if (currentStep === 3) {
      const hasFront = orderData.photos.front && (orderData.photos.front instanceof File || (typeof orderData.photos.front === 'object' && 'url' in orderData.photos.front && orderData.photos.front.url));
      const hasBack = orderData.photos.back && (orderData.photos.back instanceof File || (typeof orderData.photos.back === 'object' && 'url' in orderData.photos.back && orderData.photos.back.url));
      const hasSide = orderData.photos.side && (orderData.photos.side instanceof File || (typeof orderData.photos.side === 'object' && 'url' in orderData.photos.side && orderData.photos.side.url));
      
      if (!hasFront || !hasBack || !hasSide) {
        setError('Please upload Front, Back, and Side view photos before proceeding.');
        return;
      }
    }
    
    setError(''); // Clear any previous errors
    
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!token) {
      setError('Authentication required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Update customer with measurements and photos if customer_id exists
      // The backend endpoint now handles both user IDs (from /api/tailors/customers) 
      // and legacy Customer table IDs, so this should work correctly
      if (orderData.customer_id) {
        // Extract photo URLs (handle both File objects and URL objects)
        const getPhotoUrl = (photo: File | { url?: string; name?: string } | null): string | null => {
          if (!photo) return null;
          if (photo instanceof File) {
            // File objects need to be uploaded separately - skip for now
            // TODO: Upload File objects and get URLs
            return null;
          }
          if (typeof photo === 'object' && 'url' in photo && photo.url) {
            return photo.url;
          }
          return null;
        };

        const customerUpdateData = {
          measurements: Object.fromEntries(
            Object.entries(orderData.measurements).map(([key, value]) => [key, value])
          ),
          photo_front_url: getPhotoUrl(orderData.photos.front),
          photo_back_url: getPhotoUrl(orderData.photos.back),
          photo_side_url: getPhotoUrl(orderData.photos.side)
        };
        
        // Try to update customer, but don't block order creation if it fails
        // (e.g., if measurements/photos aren't critical for order creation)
        try {
          const customerUpdateResponse = await fetch(`${API_BASE}/api/customers/${orderData.customer_id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(customerUpdateData)
          });

          if (!customerUpdateResponse.ok) {
            const errorText = await customerUpdateResponse.text().catch(() => 'Unknown error');
            console.warn(`Customer update failed (${customerUpdateResponse.status}):`, errorText);
            // Don't throw - allow order creation to proceed
          }
        } catch (_error) {
          // Network or other errors - log but don't block order creation
          console.warn('Failed to update customer data (non-blocking):', error);
        }
      }

      // Extract fabric/cloth type from garments for notes
      const fabricTypes = orderData.garments
        .filter(g => g.material_name && g.type)
        .map(g => {
          const materialName = g.material_name || 'Unknown Material';
          return `${g.type}: ${materialName}${g.color ? ` (${g.color})` : ''}`;
        })
        .join(', ');
      
      // Build description/notes with fabric info
      const notesParts = [];
      if (orderData.special_instructions) {
        notesParts.push(orderData.special_instructions);
      }
      if (fabricTypes) {
        notesParts.push(`Fabric/Cloth: ${fabricTypes}`);
      }
      const combinedNotes = notesParts.join('. ');
      
      // Get garment type - use first garment's type or fallback to garment_type
      const firstGarmentType = orderData.garments.find(g => g.type)?.type;
      const garmentType = firstGarmentType || orderData.garment_type;
      
      // Convert File objects to base64 strings for photos
      const convertFileToBase64 = (file: File | null | { url?: string }): Promise<string | null> => {
        return new Promise((resolve) => {
          if (!file) {
            resolve(null);
            return;
          }
          
          // If it's already a URL object, return null (will be handled separately)
          if (typeof file === 'object' && 'url' in file && file.url) {
            resolve(null);
            return;
          }
          
          // If it's a File object, convert to base64
          if (file instanceof File) {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64String = reader.result as string;
              resolve(base64String);
            };
            reader.onerror = () => {
              console.error('Error converting file to base64');
              resolve(null);
            };
            reader.readAsDataURL(file);
          } else {
            resolve(null);
          }
        });
      };
      
      // Convert all photos to base64
      const photosBase64: any = {
        front: await convertFileToBase64(orderData.photos.front),
        back: await convertFileToBase64(orderData.photos.back),
        side: await convertFileToBase64(orderData.photos.side),
        optional: await Promise.all(
          (orderData.photos.optional || []).map(file => convertFileToBase64(file))
        ).then(results => results.filter(r => r !== null))
      };
      
      // Remove empty/null values from photos object
      const processedPhotos: any = {};
      if (photosBase64.front) processedPhotos.front = { base64: photosBase64.front };
      if (photosBase64.back) processedPhotos.back = { base64: photosBase64.back };
      if (photosBase64.side) processedPhotos.side = { base64: photosBase64.side };
      if (photosBase64.optional && photosBase64.optional.length > 0) {
        processedPhotos.optional = photosBase64.optional.map((b64: string) => ({ base64: b64 }));
      }
      
      const submitData = {
        ...orderData,
        garment_type: garmentType, // Map to backend expected field
        customer_phone: orderData.customer_phone || '',
        customer_address: orderData.customer_address || {},
        amount: orderData.total_amount || orderData.amount || calculateAmount(),
        due_date: orderData.due_date || calculateDueDate(orderData.priority),
        tailor_id: user?.id,
        status: 'pending', // Match production - backend will convert to OrderStatus.CREATED
        description: combinedNotes || `Order for ${garmentType || 'custom garment'}`,
        special_instructions: combinedNotes, // Include fabric info in special instructions
        measurements: Object.fromEntries(
          Object.entries(orderData.measurements).map(([key, value]) => [key, parseFloat(value) || 0])
        ),
        photos: Object.keys(processedPhotos).length > 0 ? processedPhotos : undefined // Only include if there are photos
      };

      const response = await fetch(`${API_BASE}/api/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const newOrder = await response.json();
        onSubmit(newOrder);
        setOrderCreated(true);
        setCurrentStep(6); // Go to QR code sharing step
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create order');
      }
    } catch (_error) {
      console.error('Error creating order:', error);
      setError('Failed to create order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Customer Selection</h2>
        <p className="text-gray-600">Choose an existing customer or add new customer details</p>
      </div>

      {/* Existing Customers */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Select Existing Customer</label>
        {loadingCustomers ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading customers...</p>
          </div>
        ) : customers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto">
            {customers.map((customer) => (
              <button
                key={customer.id}
                onClick={() => handleCustomerSelect(customer)}
                className={`p-3 text-left border rounded-lg hover:border-blue-500 transition-colors ${
                  orderData.customer_id === customer.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200'
                }`}
              >
                <div className="font-medium text-gray-900">{customer.name}</div>
                {customer.phone && <div className="text-sm text-gray-600">{customer.phone}</div>}
                {customer.email && <div className="text-sm text-gray-600">{customer.email}</div>}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <User className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No customers found. Add customer details below.</p>
          </div>
        )}
      </div>

      {/* Add New Customer Button */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Or Add New Customer</h3>
        </div>
        <button
          onClick={() => setIsAddCustomerModalOpen(true)}
          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-700"
        >
          <Plus className="w-5 h-5" />
          <span className="font-medium">Add New Customer</span>
        </button>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Create a new customer account with comprehensive details
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Add/Edit Measurements</h2>
        <p className="text-gray-600">Enter measurements for the customer</p>
        {loadingMeasurements && (
          <div className="mt-2 flex items-center justify-center gap-2 text-sm text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span>Loading existing measurements...</span>
          </div>
        )}
        {savingMeasurements && (
          <div className="mt-2 flex items-center justify-center gap-2 text-sm text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span>Saving measurements...</span>
          </div>
        )}
        {measurementsSaved && !savingMeasurements && (
          <div className="mt-2 flex items-center justify-center gap-2 text-sm text-green-600">
            <Check className="w-4 h-4" />
            <span>Measurements saved successfully!</span>
          </div>
        )}
      </div>

      {/* Customer-Level Measurements */}
      <MeasurementForm
        values={orderData.measurements as MeasurementData}
        onChange={(measurements) => {
          setOrderData(prev => ({
            ...prev,
            measurements: measurements as any
          }));
        }}
        showLanguageSwitcher={true}
        showSectionHeaders={true}
        layout="grid"
      />

      {/* Special Instructions */}
      <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Additional Notes</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
          <textarea
            value={orderData.special_instructions}
            onChange={(e) => handleInputChange('special_instructions', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white text-gray-900"
            placeholder="Any special requirements, style notes, or additional instructions..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Customer Photos</h2>
        <p className="text-gray-600">Upload Front, Back, and Side view photos</p>
      </div>

      {/* Customer Photos */}
      <div className="border border-gray-200 rounded-lg p-4 bg-purple-50">
        <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
          <Camera className="w-5 h-5" />
          Customer Photos
        </h3>
        
        {/* Photo Upload Section - Three Frames: One for Each View */}
        <div className="space-y-6 mb-4">
          {/* Front View Frame */}
          <div className="p-4 bg-white rounded-lg border border-purple-200">
            <h4 className="text-sm font-medium text-purple-900 mb-4">Front View *</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Example Photo */}
              <div>
                <label className="block text-xs text-gray-600 mb-2">Example Photo</label>
                <a href="/examples/S_Front.png" target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                  <div className="relative w-full h-56 bg-gray-100 rounded border-2 border-dashed border-purple-300 flex items-center justify-center hover:bg-purple-50 transition-colors overflow-hidden">
                    <img src="/examples/S_Front.png" alt="Front view example" className="w-full h-full object-contain rounded" onError={(e) => {
                      e.currentTarget.parentElement!.innerHTML = '<div class="text-xs text-gray-500 p-2">Front View Example</div>';
                    }} />
                  </div>
                </a>
              </div>
              
              {/* Upload Area */}
              <div>
                <label className="block text-xs text-gray-600 mb-2">Upload Your Photo</label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload('front', e.target.files?.[0] || null)}
                    className="hidden"
                    id="photo-front"
                  />
                  <label
                    htmlFor="photo-front"
                    className="flex flex-col items-center justify-center w-full h-56 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
                  >
                    {orderData.photos.front ? (
                      <div className="relative w-full h-full">
                        <img
                          src={getPhotoUrl(orderData.photos.front) || ''}
                          alt="Front view"
                          className="w-full h-full object-contain rounded-lg"
                        />
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handlePhotoUpload('front', null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-purple-400 mb-2" />
                        <span className="text-sm text-purple-600">Click to upload</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Back View Frame */}
          <div className="p-4 bg-white rounded-lg border border-purple-200">
            <h4 className="text-sm font-medium text-purple-900 mb-4">Back View *</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Example Photo */}
              <div>
                <label className="block text-xs text-gray-600 mb-2">Example Photo</label>
                <a href="/examples/S_Back.png" target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                  <div className="relative w-full h-56 bg-gray-100 rounded border-2 border-dashed border-purple-300 flex items-center justify-center hover:bg-purple-50 transition-colors overflow-hidden">
                    <img src="/examples/S_Back.png" alt="Back view example" className="w-full h-full object-contain rounded" onError={(e) => {
                      e.currentTarget.parentElement!.innerHTML = '<div class="text-xs text-gray-500 p-2">Back View Example</div>';
                    }} />
                  </div>
                </a>
              </div>
              
              {/* Upload Area */}
              <div>
                <label className="block text-xs text-gray-600 mb-2">Upload Your Photo</label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload('back', e.target.files?.[0] || null)}
                    className="hidden"
                    id="photo-back"
                  />
                  <label
                    htmlFor="photo-back"
                    className="flex flex-col items-center justify-center w-full h-56 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
                  >
                    {orderData.photos.back ? (
                      <div className="relative w-full h-full">
                        <img
                          src={getPhotoUrl(orderData.photos.back) || ''}
                          alt="Back view"
                          className="w-full h-full object-contain rounded-lg"
                        />
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handlePhotoUpload('back', null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-purple-400 mb-2" />
                        <span className="text-sm text-purple-600">Click to upload</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Side View Frame */}
          <div className="p-4 bg-white rounded-lg border border-purple-200">
            <h4 className="text-sm font-medium text-purple-900 mb-4">Side View *</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Example Photo */}
              <div>
                <label className="block text-xs text-gray-600 mb-2">Example Photo</label>
                <a href="/examples/S_Side.png" target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                  <div className="relative w-full h-56 bg-gray-100 rounded border-2 border-dashed border-purple-300 flex items-center justify-center hover:bg-purple-50 transition-colors overflow-hidden">
                    <img src="/examples/S_Side.png" alt="Side view example" className="w-full h-full object-contain rounded" onError={(e) => {
                      e.currentTarget.parentElement!.innerHTML = '<div class="text-xs text-gray-500 p-2">Side View Example</div>';
                    }} />
                  </div>
                </a>
              </div>
              
              {/* Upload Area */}
              <div>
                <label className="block text-xs text-gray-600 mb-2">Upload Your Photo</label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload('side', e.target.files?.[0] || null)}
                    className="hidden"
                    id="photo-side"
                  />
                  <label
                    htmlFor="photo-side"
                    className="flex flex-col items-center justify-center w-full h-56 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
                  >
                    {orderData.photos.side ? (
                      <div className="relative w-full h-full">
                        <img
                          src={getPhotoUrl(orderData.photos.side) || ''}
                          alt="Side view"
                          className="w-full h-full object-contain rounded-lg"
                        />
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handlePhotoUpload('side', null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-purple-400 mb-2" />
                        <span className="text-sm text-purple-600">Click to upload</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Helper Text */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-gray-600">These photos are required to ensure accurate measurements. Please capture full-body shots against a plain background.</p>
        </div>

        {/* Optional Photos */}
        <div>
          <h4 className="text-sm font-medium text-purple-900 mb-3">Optional Additional Views</h4>
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleOptionalPhotoUpload(e.target.files)}
              className="hidden"
              id="photo-optional"
            />
            <label
              htmlFor="photo-optional"
              className="flex items-center justify-center w-full h-32 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
            >
              {orderData.photos.optional.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {Array.from(orderData.photos.optional).map((file, index) => (
                    <div key={index} className="relative w-28 h-28">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Optional ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          const newOptional = orderData.photos.optional.filter((_, i) => i !== index);
                          setOrderData(prev => ({
                            ...prev,
                            photos: {
                              ...prev.photos,
                              optional: newOptional
                            }
                          }));
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <div className="w-28 h-28 border-2 border-dashed border-purple-300 rounded-lg flex items-center justify-center cursor-pointer hover:bg-purple-50">
                    <Plus className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
              ) : (
                <>
                  <Camera className="w-8 h-8 text-purple-400 mr-2" />
                  <span className="text-sm text-purple-600">Click to add more photos</span>
                </>
              )}
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Garment Selection</h2>
        <p className="text-gray-600">Select garments and materials from your inventory</p>
      </div>

      {/* Garments List */}
      <div className="space-y-4">
        {orderData.garments.map((garment, index) => (
          <div key={garment.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Garment {index + 1}</h3>
              {orderData.garments.length > 1 && (
                <button
                  onClick={() => removeGarment(garment.id)}
                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Garment Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Garment Type *</label>
                <select
                  value={garment.type}
                  onChange={(e) => updateGarment(garment.id, 'type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                >
                  <option value="">Select garment type</option>
                  {garmentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.icon} {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Material Selection from Inventory */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Material from Inventory *</label>
                </div>
                {inventory.length === 0 && !loadingInventory ? (
                  // Show prominent button when inventory is empty
                  <div className="space-y-3">
                    <div className="border-2 border-dashed border-amber-300 rounded-lg p-4 bg-amber-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                          <div>
                            <p className="text-sm font-medium text-amber-900">No materials in inventory</p>
                            <p className="text-xs text-amber-700">Add a material to continue with order creation</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsInventoryModalOpen(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                          title="Add new material to inventory"
                        >
                          <Plus className="w-4 h-4" />
                          Add Material
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Show dropdown when inventory exists - only unique material names
                  <div className="flex gap-2">
                    <select
                      value={garment.material_name}
                      onChange={(e) => updateGarment(garment.id, 'material_name', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                      disabled={loadingInventory}
                    >
                      <option value="">Select material</option>
                      {Array.from(new Set(inventory.map(item => item.name))).map((materialName) => (
                        <option key={materialName} value={materialName}>
                          {materialName}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setIsInventoryModalOpen(true)}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm"
                      title="Add new material to inventory"
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                )}
                {loadingInventory && (
                  <p className="text-sm text-gray-500 mt-1">Loading inventory...</p>
                )}
              </div>

              {/* Price per Unit */}
              {garment.material_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price per Unit *</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={garment.price_per_unit === 0 ? '' : garment.price_per_unit}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '0') {
                          updateGarment(garment.id, 'price_per_unit', 0);
                        } else {
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue) && numValue >= 0) {
                            updateGarment(garment.id, 'price_per_unit', numValue);
                          }
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                      placeholder="0.00"
                    />
                    <select
                      value={garment.price_unit}
                      onChange={(e) => updateGarment(garment.id, 'price_unit', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                    >
                      <option value="meters">meters</option>
                      <option value="unit">unit</option>
                      <option value="piece">piece</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Color - filtered by selected material */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                {garment.material_name ? (
                  <select
                    value={garment.color}
                    onChange={(e) => updateGarment(garment.id, 'color', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  >
                    <option value="">Select color</option>
                    {Array.from(new Set(
                      inventory
                        .filter(item => item.name === garment.material_name && item.color)
                        .map(item => item.color)
                    )).map((color) => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={garment.color}
                    onChange={(e) => updateGarment(garment.id, 'color', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                    placeholder="Select material first"
                    disabled
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity ({garment.price_unit === 'unit' ? 'units' : garment.price_unit === 'piece' ? 'pieces' : garment.price_unit || 'meters'}) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={garment.quantity === 0 || garment.quantity === undefined ? '' : garment.quantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || value === '0') {
                      updateGarment(garment.id, 'quantity', 0);
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        updateGarment(garment.id, 'quantity', numValue);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Ensure value is formatted to 3 decimals on blur
                    if (e.target.value === '' || e.target.value === '0') {
                      updateGarment(garment.id, 'quantity', 0);
                    } else {
                      const numValue = parseFloat(e.target.value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        updateGarment(garment.id, 'quantity', parseFloat(numValue.toFixed(3)));
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{ MozAppearance: 'textfield' }}
                  placeholder="0.000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stitching Cost *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={garment.stitching_cost === 0 || garment.stitching_cost === undefined ? '' : garment.stitching_cost}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Handle empty string or valid number
                    if (value === '' || value === '0') {
                      updateGarment(garment.id, 'stitching_cost', 0);
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        // Round to 2 decimals
                        const roundedValue = Math.round(numValue * 100) / 100;
                        updateGarment(garment.id, 'stitching_cost', roundedValue);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Ensure value is rounded to 2 decimals on blur
                    if (e.target.value === '' || e.target.value === '0') {
                      updateGarment(garment.id, 'stitching_cost', 0);
                    } else {
                      const numValue = parseFloat(e.target.value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        const roundedValue = Math.round(numValue * 100) / 100;
                        updateGarment(garment.id, 'stitching_cost', roundedValue);
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  style={{ MozAppearance: 'textfield' }}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Cost Breakdown */}
            {garment.type && garment.material_name && (
              <div className="mt-4 p-3 bg-white rounded-lg border">
                <h4 className="font-medium text-gray-900 mb-2">Cost Breakdown</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Material Cost:</span>
                    <div className="font-medium">₹{garment.material_cost.toFixed(2)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Stitching Cost:</span>
                    <div className="font-medium">₹{garment.stitching_cost.toFixed(2)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Total:</span>
                    <div className="font-bold text-blue-600">₹{garment.total_cost.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Garment Button */}
      <div className="text-center">
        <button
          onClick={addGarment}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Garment
        </button>
      </div>

      {/* Order Summary */}
      {orderData.garments.some(g => g.type && g.material_name) && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Order Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-700">Subtotal:</span>
              <div className="font-medium text-blue-900">₹{orderData.subtotal.toFixed(3)}</div>
            </div>
            {orderData.include_gst && orderData.tax_amount > 0 && (
              <div>
                <span className="text-blue-700">GST (5%):</span>
                <div className="font-medium text-blue-900">₹{orderData.tax_amount.toFixed(3)}</div>
              </div>
            )}
            <div>
              <span className="text-blue-700">Total:</span>
              <div className="font-bold text-blue-900 text-lg">₹{orderData.total_amount.toFixed(3)}</div>
            </div>
            <div>
              <span className="text-blue-700">Garments:</span>
              <div className="font-medium text-blue-900">{orderData.garments.filter(g => g.type).length}</div>
            </div>
          </div>
          {!orderData.include_gst && (
            <div className="mt-2 text-xs text-blue-600">
              * GST not included (unchecked above)
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Review & Priority</h2>
        <p className="text-gray-600">Review your order details and set priority</p>
      </div>

      {/* Order Summary */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
        
        {/* Customer Info */}
        <div className="mb-4 p-3 bg-white rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Customer</h4>
          <p className="text-gray-700">{orderData.customer_name}</p>
          <p className="text-gray-600 text-sm">{orderData.customer_email}</p>
          {orderData.customer_phone && (
            <p className="text-gray-600 text-sm">{orderData.customer_phone}</p>
          )}
        </div>

        {/* Garments */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Garments</h4>
          {orderData.garments.map((garment, index) => {
            if (!garment.type || !garment.material_name) return null;
            
            const garmentType = garmentTypes.find(gt => gt.id === garment.type);
            
            return (
              <div key={garment.id} className="p-3 bg-white rounded-lg border">
                <div className="flex justify-between items-start mb-2">
                  <h5 className="font-medium text-gray-900">
                    {garmentType?.name} - {garment.color || 'No color specified'}
                  </h5>
                  <span className="text-sm text-gray-600">Qty: {garment.quantity.toFixed(3)}</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Material: {garment.material_name || 'Unknown material'}
                </p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Cost per unit:</span>
                  <span className="font-medium">₹{garment.total_cost.toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Total Cost */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-700">Subtotal:</span>
            <span className="font-medium">₹{orderData.subtotal.toLocaleString()}</span>
          </div>
          {orderData.include_gst && orderData.tax_amount > 0 && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700">GST (5%):</span>
              <span className="font-medium">₹{orderData.tax_amount.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between items-center border-t border-blue-200 pt-2">
            <span className="font-semibold text-blue-900">Total:</span>
            <span className="font-bold text-blue-900 text-lg">₹{orderData.total_amount.toLocaleString()}</span>
          </div>
          {!orderData.include_gst && (
            <div className="mt-2 text-xs text-blue-600">
              * GST not included (unchecked above)
            </div>
          )}
        </div>
      </div>

      {/* Priority and Due Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority *</label>
          <select
            value={orderData.priority}
            onChange={(e) => {
              handleInputChange('priority', e.target.value);
              handleInputChange('due_date', calculateDueDate(e.target.value));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
          >
            {priorities.map((priority) => (
              <option key={priority.id} value={priority.id}>
                {priority.name} - {priority.days} days
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
          <input
            type="date"
            value={orderData.due_date}
            onChange={(e) => handleInputChange('due_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
          />
        </div>
      </div>

      {/* GST Inclusion */}
      <div className="mt-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={orderData.include_gst}
            onChange={(e) => {
              handleInputChange('include_gst', e.target.checked);
            }}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <span className="text-sm font-medium text-gray-700">
            Include GST (5%) in pricing
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1">
          Check this box if you file taxes and need to include GST in the order total
        </p>
      </div>

      {/* Special Instructions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
        <textarea
          value={orderData.special_instructions}
          onChange={(e) => handleInputChange('special_instructions', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white text-gray-900"
          placeholder="Any special requirements or notes for this order..."
        />
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Created Successfully!</h2>
        <p className="text-gray-600">Share the app with your customer for order tracking</p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Check className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-semibold text-green-800">Order Details</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p><strong>Customer:</strong> {orderData.customer_name}</p>
            <p><strong>Garment:</strong> {
              orderData.garments && orderData.garments.length > 0 && orderData.garments.some(g => g.type)
                ? orderData.garments
                    .filter(g => g.type)
                    .map(g => {
                      const garmentType = garmentTypes.find(gt => gt.id === g.type);
                      return garmentType?.name || g.type;
                    })
                    .join(', ')
                : orderData.garment_type
                  ? garmentTypes.find(g => g.id === orderData.garment_type)?.name || orderData.garment_type
                  : 'Not specified'
            }</p>
            <p><strong>Amount:</strong> ₹{
              orderData.total_amount > 0 
                ? orderData.total_amount.toFixed(2)
                : orderData.amount > 0
                  ? orderData.amount.toFixed(2)
                  : calculateAmount() > 0
                    ? calculateAmount().toFixed(2)
                    : '0.00'
            }</p>
          </div>
          <div>
            <p><strong>Due Date:</strong> {orderData.due_date || calculateDueDate(orderData.priority)}</p>
            <p><strong>Priority:</strong> {priorities.find(p => p.id === orderData.priority)?.name}</p>
            <p><strong>Status:</strong> Pending</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          Share Masterji App with Customer
        </h3>
        
        <div className="space-y-4">
          <div className="text-center">
            <div className="bg-white rounded-lg p-4 inline-block">
              {qrCodeDataUrl ? (
                <img 
                  src={qrCodeDataUrl} 
                  alt="App Download QR Code" 
                  className="w-32 h-32 mx-auto"
                />
              ) : (
                <div className="w-32 h-32 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-2">Customer can scan this QR code to download the app</p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="font-medium">Email Link</p>
                <p className="text-sm text-gray-600">
                  {emailSent ? 'Email sent successfully!' : 'Send download link via email'}
                </p>
              </div>
              <button 
              onClick={handleSendEmail}
              disabled={sendingEmail || !orderData.customer_email}
              className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {sendingEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : emailSent ? (
                  <>
                    <Check className="w-4 h-4" />
                    Sent
                  </>
                ) : (
                  'Send Email'
                )}
              </button>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
              <Share2 className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="font-medium">Share Link</p>
                <p className="text-sm text-gray-600">
                  {linkCopied ? 'Link copied to clipboard!' : 'Copy link to share via WhatsApp/SMS'}
                </p>
              </div>
              <button 
              onClick={handleCopyLink}
              disabled={!appDownloadLink}
              className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  'Copy Link'
                )}
              </button>
            </div>
            
            {appDownloadLink && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Download Link:</p>
                <p className="text-xs text-blue-600 break-all">{appDownloadLink}</p>
              </div>
            )}
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">What customers can do in the app:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Track order progress in real-time</li>
              <li>• View order details and measurements</li>
              <li>• Communicate with tailor via chat</li>
              <li>• Schedule appointments and fittings</li>
              <li>• Receive notifications for updates</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1: return renderStep1(); // Customer Selection
      case 2: return renderStep2(); // Measurements
      case 3: return renderStep3(); // Photo Upload
      case 4: return renderStep4(); // Garment Selection
      case 5: return renderStep5(); // Order Review & Priority
      case 6: return renderStep6(); // Order Created Successfully
      default: return renderStep1();
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1: return orderData.customer_name.trim() !== '';
      case 2: return Object.values(orderData.measurements).some(m => m !== '' && m !== null && m !== undefined); // At least one measurement
      case 3: {
        // Check if photos exist (either File object or URL)
        const hasFront = orderData.photos.front && (orderData.photos.front instanceof File || (typeof orderData.photos.front === 'object' && 'url' in orderData.photos.front && orderData.photos.front.url));
        const hasBack = orderData.photos.back && (orderData.photos.back instanceof File || (typeof orderData.photos.back === 'object' && 'url' in orderData.photos.back && orderData.photos.back.url));
        const hasSide = orderData.photos.side && (orderData.photos.side instanceof File || (typeof orderData.photos.side === 'object' && 'url' in orderData.photos.side && orderData.photos.side.url));
        return hasFront && hasBack && hasSide; // All photos required
      }
      case 4: {
        // Validate all garments have required fields (matching production logic)
        // Production uses: g.stitching_cost >= 0 (allows 0)
        const hasValidGarments = orderData.garments.some(g => {
          const hasType = g.type !== null && g.type !== undefined && g.type !== '';
          const hasMaterialName = g.material_name !== null && g.material_name !== undefined && g.material_name !== '';
          const hasPricePerUnit = typeof g.price_per_unit === 'number' && g.price_per_unit > 0;
          const hasQuantity = typeof g.quantity === 'number' && g.quantity > 0;
          const hasStitchingCost = typeof g.stitching_cost === 'number' && g.stitching_cost >= 0; // Allow 0 like production
          return hasType && hasMaterialName && hasPricePerUnit && hasQuantity && hasStitchingCost;
        });
        // Also check that all garments either have no data or are complete (no partial garments)
        const allGarmentsValid = orderData.garments.every(g => {
          // If garment has any field filled, all required fields must be filled
          const hasAnyData = (g.type !== null && g.type !== undefined && g.type !== '') || 
                           (g.material_name !== null && g.material_name !== undefined && g.material_name !== '') || 
                           (typeof g.price_per_unit === 'number' && g.price_per_unit > 0) ||
                           (typeof g.quantity === 'number' && g.quantity > 0) || 
                           (typeof g.stitching_cost === 'number' && g.stitching_cost >= 0);
          if (!hasAnyData) return true; // Empty garment is OK
          // If has data, all required fields must be present
          const hasType = g.type !== null && g.type !== undefined && g.type !== '';
          const hasMaterialName = g.material_name !== null && g.material_name !== undefined && g.material_name !== '';
          const hasPricePerUnit = typeof g.price_per_unit === 'number' && g.price_per_unit > 0;
          const hasQuantity = typeof g.quantity === 'number' && g.quantity > 0;
          const hasStitchingCost = typeof g.stitching_cost === 'number' && g.stitching_cost >= 0; // Allow 0 like production
          return hasType && hasMaterialName && hasPricePerUnit && hasQuantity && hasStitchingCost;
        });
        console.log('🔍 Step 4 validation:', { hasValidGarments, allGarmentsValid, garments: orderData.garments });
        return hasValidGarments && allGarmentsValid;
      }
      case 5: return orderData.priority !== '' && orderData.due_date !== '';
      case 6: return true; // Order created step
      default: return false;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-4xl bg-white rounded-2xl shadow-xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Create New Order</h1>
              <p className="text-sm text-gray-600">Step {currentStep} of 6</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div
                key={step}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {step < currentStep ? <Check className="w-4 h-4" /> : step}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderStep()}
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-200">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            
            {currentStep === 5 ? (
              <button
                onClick={handleSubmit}
                disabled={!isStepValid() || isSubmitting}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Create Order
                  </>
                )}
              </button>
            ) : currentStep === 6 ? (
              <button
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Done
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!isStepValid()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={isAddCustomerModalOpen}
        onClose={() => setIsAddCustomerModalOpen(false)}
        onCustomerCreated={handleCustomerCreated}
        mode="order"
      />

      {/* Add Inventory Modal */}
      {isInventoryModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" onClick={() => setIsInventoryModalOpen(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Add Material to Inventory</h2>
                <p className="text-sm text-gray-600">Create a new material for your inventory</p>
              </div>
              <button
                onClick={() => setIsInventoryModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Material Name *
                </label>
                <input
                  type="text"
                  value={inventoryFormData.name}
                  onChange={(e) => setInventoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  placeholder="e.g., Cotton Fabric, Silk Material"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={inventoryFormData.category}
                  onChange={(e) => setInventoryFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                >
                  <option value="fabric">Fabric</option>
                  <option value="thread">Thread</option>
                  <option value="buttons">Buttons</option>
                  <option value="zipper">Zipper</option>
                  <option value="accessories">Accessories</option>
                  <option value="tools">Tools</option>
                  <option value="machines">Machines</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Stock
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={inventoryFormData.current_stock || ''}
                    onChange={(e) => setInventoryFormData(prev => ({ ...prev, current_stock: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit *
                  </label>
                  <select
                    value={inventoryFormData.unit}
                    onChange={(e) => setInventoryFormData(prev => ({ ...prev, unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  >
                    <option value="meters">Meters</option>
                    <option value="yards">Yards</option>
                    <option value="pieces">Pieces</option>
                    <option value="rolls">Rolls</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost per Unit
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={inventoryFormData.cost_per_unit || ''}
                    onChange={(e) => setInventoryFormData(prev => ({ ...prev, cost_per_unit: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Selling Price
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={inventoryFormData.selling_price || ''}
                    onChange={(e) => setInventoryFormData(prev => ({ ...prev, selling_price: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Colors Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Available Colors
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Enter colors separated by commas or press Enter/Add button. Each color will create a separate inventory item.
                </p>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="color-input"
                      placeholder="e.g., Navy Blue, Black, White"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const input = e.currentTarget;
                          const value = input.value.trim();
                          if (value) {
                            // Split by comma and add each color
                            const colorsToAdd = value.split(',').map(c => c.trim()).filter(c => c && !inventoryFormData.colors.includes(c));
                            if (colorsToAdd.length > 0) {
                              setInventoryFormData(prev => ({
                                ...prev,
                                colors: [...prev.colors, ...colorsToAdd]
                              }));
                            }
                            input.value = '';
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value) {
                          // Split by comma and add each color
                          const colorsToAdd = value.split(',').map(c => c.trim()).filter(c => c && !inventoryFormData.colors.includes(c));
                          if (colorsToAdd.length > 0) {
                            setInventoryFormData(prev => ({
                              ...prev,
                              colors: [...prev.colors, ...colorsToAdd]
                            }));
                          }
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('color-input') as HTMLInputElement;
                        if (input) {
                          const value = input.value.trim();
                          if (value) {
                            // Split by comma and add each color
                            const colorsToAdd = value.split(',').map(c => c.trim()).filter(c => c && !inventoryFormData.colors.includes(c));
                            if (colorsToAdd.length > 0) {
                              setInventoryFormData(prev => ({
                                ...prev,
                                colors: [...prev.colors, ...colorsToAdd]
                              }));
                            }
                            input.value = '';
                          }
                        }
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Add
                    </button>
                  </div>
                  
                  {/* Display color tags */}
                  {inventoryFormData.colors.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {inventoryFormData.colors.map((color, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {color}
                          <button
                            type="button"
                            onClick={() => {
                              setInventoryFormData(prev => ({
                                ...prev,
                                colors: prev.colors.filter((_, i) => i !== index)
                              }));
                            }}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start gap-2 text-red-800">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="text-sm whitespace-pre-line">{error}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button
                type="button"
                onClick={() => {
                  setIsInventoryModalOpen(false);
                  setError('');
                  setInventoryFormData({
                    name: '',
                    category: 'fabric',
                    current_stock: 0,
                    unit: 'pieces',
                    selling_price: 0,
                    cost_per_unit: 0,
                    colors: []
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isCreatingInventory}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateInventory}
                disabled={isCreatingInventory || !inventoryFormData.name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isCreatingInventory ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Material
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default BusinessOrderForm;
